/* PGEmployment.js
   Step 11 — Current Employment (PG-specific).
   SOW requirements:
   - Radio buttons (not checkboxes)
   - Three options:
     1. Currently Unemployed
     2. Employed → show: Company Name, Position, Start Date, Brief of Duties
     3. Currently employed as TKH/Coventry staff → show: Employer ID Number
   Stored in USERDEFINEDIND via save payload extension.
*/
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import InputRow from './InputRow'

const RADIO_STYLE = { minWidth: 'auto', marginRight: 8, cursor: 'pointer' }

export default class PGEmployment extends Component {
  constructor (props) {
    super(props)
    const data = props.employmentData || {}
    this.state = {
      employmentStatus: data.employmentStatus || '',  // 'Unemployed' | 'Employed' | 'TKHStaff'
      companyName: data.companyName || '',
      position: data.position || '',
      startDate: data.startDate || '',
      briefOfDuties: data.briefOfDuties || '',
      employerIdNumber: data.employerIdNumber || ''
    }
    this.publish = this.publish.bind(this)
  }

  componentDidMount () {
    /* Push initial state to Main.js so save works even if user never changes a field */
    this.publish()
  }

  publish (override = {}) {
    const s = { ...this.state, ...override }
    this.props.collectState('PG_Emp', {
      employmentStatus: s.employmentStatus,
      companyName: s.companyName,
      position: s.position,
      startDate: s.startDate,
      briefOfDuties: s.briefOfDuties,
      employerIdNumber: s.employerIdNumber
    })
  }

  handleStatusChange (val) {
    /* Clear dependent fields when status changes */
    const clearedFields = {
      companyName: '',
      position: '',
      startDate: '',
      briefOfDuties: '',
      employerIdNumber: ''
    }
    this.setState({ employmentStatus: val, ...clearedFields }, () => this.publish())
  }

  render () {
    const { employmentStatus, companyName, position, startDate, briefOfDuties, employerIdNumber } = this.state
    const { error } = this.props

    return (
      <div>
        <p style={{ fontWeight: 'bold', marginBottom: 12, color: '#333' }}>
          Current Employment Status <span style={{ color: 'red' }}>*</span>
        </p>
        {error.employmentStatus && (
          <p style={{ color: 'red', fontSize: 12 }}>{error.employmentStatus}</p>
        )}

        {/* Option 1: Unemployed */}
        <div style={{ marginBottom: 10 }}>
          <label style={{ cursor: 'pointer' }}>
            <input
              type='radio'
              name='pgEmployment'
              value='Unemployed'
              checked={employmentStatus === 'Unemployed'}
              onChange={() => this.handleStatusChange('Unemployed')}
              style={RADIO_STYLE}
            />
            Currently Unemployed
          </label>
        </div>

        {/* Option 2: Employed */}
        <div style={{ marginBottom: 10 }}>
          <label style={{ cursor: 'pointer' }}>
            <input
              type='radio'
              name='pgEmployment'
              value='Employed'
              checked={employmentStatus === 'Employed'}
              onChange={() => this.handleStatusChange('Employed')}
              style={RADIO_STYLE}
            />
            Employed, please provide the current employment details:
          </label>
        </div>

        {employmentStatus === 'Employed' && (
          <div style={{ paddingLeft: 24, borderLeft: '3px solid #6AA8FF', marginBottom: 12 }}>
            <InputRow
              rowName='Company Name'
              name='text'
              value={companyName}
              required='*'
              errMsg={error.companyName || ''}
              onChange={(val) => {
                this.setState({ companyName: val }, () => this.publish())
              }}
            />
            <InputRow
              rowName='Position'
              name='text'
              value={position}
              required='*'
              errMsg={error.position || ''}
              onChange={(val) => {
                this.setState({ position: val }, () => this.publish())
              }}
            />
            <InputRow
              rowName='Start Date'
              name='date'
              value={startDate}
              required='*'
              errMsg={error.startDate || ''}
              onChange={(val) => {
                this.setState({ startDate: val }, () => this.publish())
              }}
            />
            <InputRow
              rowName='Brief of Duties'
              name='text'
              value={briefOfDuties}
              required='*'
              errMsg={error.briefOfDuties || ''}
              onChange={(val) => {
                this.setState({ briefOfDuties: val }, () => this.publish())
              }}
            />
          </div>
        )}

        {/* Option 3: TKH / Coventry Staff */}
        <div style={{ marginBottom: 10 }}>
          <label style={{ cursor: 'pointer' }}>
            <input
              type='radio'
              name='pgEmployment'
              value='TKHStaff'
              checked={employmentStatus === 'TKHStaff'}
              onChange={() => this.handleStatusChange('TKHStaff')}
              style={RADIO_STYLE}
            />
            Currently employed as a staff member at the Coventry University branch in Cairo
          </label>
        </div>

        {employmentStatus === 'TKHStaff' && (
          <div style={{ paddingLeft: 24, borderLeft: '3px solid #6AA8FF', marginBottom: 12 }}>
            <InputRow
              rowName='Employer ID Number'
              name='text'
              value={employerIdNumber}
              required='*'
              errMsg={error.employerIdNumber || ''}
              onChange={(val) => {
                if (val.length <= 20 || val === '') {
                  this.setState({ employerIdNumber: val }, () => this.publish())
                }
              }}
            />
          </div>
        )}

        <button className='save' onClick={() => { this.props.onSave() }}>Next</button>
      </div>
    )
  }
}

PGEmployment.validate = function (data) {
  const errors = {}
  if (!data.employmentStatus) {
    errors.employmentStatus = 'Please select your current employment status'
    return errors
  }
  if (data.employmentStatus === 'Employed') {
    if (!data.companyName) errors.companyName = 'Please enter company name'
    if (!data.position) errors.position = 'Please enter your position'
    if (!data.startDate) errors.startDate = 'Please select start date'
    if (!data.briefOfDuties) errors.briefOfDuties = 'Please enter brief of duties'
  }
  if (data.employmentStatus === 'TKHStaff') {
    if (!data.employerIdNumber) errors.employerIdNumber = 'Please enter your employer ID number'
  }
  return errors
}

PGEmployment.propTypes = {
  onSave: PropTypes.func.isRequired,
  collectState: PropTypes.func.isRequired,
  error: PropTypes.object,
  employmentData: PropTypes.object
}

PGEmployment.defaultProps = {
  error: {},
  employmentData: {}
}
